import React, { Dispatch, SetStateAction, useState } from "react";
import DeletePopUp from "../DeletePopUp";
import { ICooperative } from "../../exports/interfaces";
import useMobile from "../../hooks/useMobile";
import { useLoader } from "../../context/LoaderContext";
import { fetchCooperative } from "../../api/DashboardApi";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { Eye, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import { darkenColor } from "../../exports/functions";
import { useOption } from "../../context/OptionContext";
import { usePermission } from "../../hooks/usePermission";
import TableFooter from "../TableFooter";
import { useUser } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";

const Table = ({
  cooperative,
  setCooperative,
}: {
  cooperative: ICooperative[];
  setCooperative: Dispatch<SetStateAction<ICooperative[]>>;
}) => {
  const { setLoading } = useLoader();
  const { canEdit, canDelete } = usePermission();
  const { user } = useUser();
  const { sendNotification } = useNotification();
  const isMobile = useMobile();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);

  const draftCooperatives =
    cooperative?.filter((item) => item.status?.toLowerCase() === "draft") || [];

  const itemsPerPage = showAll ? Math.max(draftCooperatives.length, 1) : 5;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = draftCooperatives.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  const totalPages = Math.ceil(draftCooperatives.length / itemsPerPage);
  const handleClose = () => {
    setShowDelete(false);
  };
  const { statusOptions, categories } = useOption();

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (deleteId === null) return;

      const response = await axiosClient.delete(`/registers/${deleteId}`);
      if (response.status === 200 || response.status === 201) {
        fetchCooperative()
          .then((cooperativeData) => {
            setCooperative(cooperativeData);
            setShowDelete(false);
            setDeleteId(null);
            sendNotification(
              `Cooperative deleted`,
              user?.user?.userName || "User",
            );
            setTimeout(() => {
              setLoading(false);
              dynamicSuccess("Cooperative deleted successfully!");
            }, 1000);
          })
          .catch((error) => {
            const message =
              error?.response?.data?.message || "Something went wrong!";
            dynamicFailed(message);
            setTimeout(() => {
              setLoading(false);
            }, 1000);
          });
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || "Something went wrong!";
      dynamicFailed(message);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const statusStyle = (status: string) => {
    const cooperativeStatuses =
      statusOptions?.find((item) => item.name === "Cooperative Statuses")
        ?.statusOptions || [];

    const matchedStatus = cooperativeStatuses.find(
      (item) => item.label === status,
    );

    return {
      backgroundColor: matchedStatus?.color || "#ccc",
      color: darkenColor(matchedStatus?.color || "#ccc", 40),
    };
  };

  const natureStyle = (nature: string) => {
    const natureOptions =
      categories?.find(
        (item) => item.name === "Nature of Work / Notice Audience",
      )?.dropdowns || [];

    const matchedNature = natureOptions.find(
      (item) => item.label?.toLowerCase() === nature?.toLowerCase(),
    );

    return {
      backgroundColor: matchedNature?.color || "#ccc",
      color: darkenColor(matchedNature?.color || "#ccc", 40),
    };
  };
  return (
    <>
      <div id="latest-registered" className="dashboard-card">
        <div className="card-body">
          <h6 className="mb-3">LATEST REGISTERED</h6>

          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th className="table-xs">S. no.</th>
                  <th className="table-sm">Reg no.</th>
                  <th className="table-md">Name</th>
                  <th className="table-sm">Reg. PAN no.</th>
                  <th className="table-md">Email Address</th>
                  <th className="table-sm">Mobile no.</th>
                  <th className="table-md">Status</th>
                  <th className="table-md">Nature of Work</th>
                  <th className="table-sm">Actions</th>
                </tr>
              </thead>

              <tbody>
                {currentData.length > 0 ? (
                  currentData.map((item, i) => (
                    <tr key={i}>
                      <td
                        className="table-xs text-danger"
                        title={String(indexOfFirstItem + i + 1)}
                      >
                        {String(indexOfFirstItem + i + 1).padStart(2, "0")}
                      </td>

                      <td
                        className="table-sm text-danger"
                        title={item.registrationNumber}
                      >
                        {item.registrationNumber}
                      </td>
                      <td
                        className="table-md table-bold"
                        title={item.nameEnglish || "-"}
                      >
                        {item.nameEnglish || "-"}
                      </td>
                      <td className="table-sm" title={item.panNumber || "-"}>
                        {item.panNumber || "-"}
                      </td>
                      <td className="table-md" title={item.email || "-"}>
                        {item.email || "-"}
                      </td>
                      <td className="table-sm" title={item.mobileNumber || "-"}>
                        {item.mobileNumber || "-"}
                      </td>

                      <td className="table-md">
                        <span
                          className="badge rounded-pill px-3 py-2 mw-100"
                          style={statusStyle(item.status || "Draft")}
                          title={item.status || "Draft"}
                        >
                          {item.status || "Draft"}
                        </span>
                      </td>

                      <td className="table-md">
                        <span
                          className="badge rounded-pill px-3 py-2 mw-100"
                          style={natureStyle(item.natureOfWork)}
                          title={item.natureOfWork || "-"}
                        >
                          {item.natureOfWork || "-"}
                        </span>
                      </td>

                      <td className="table-sm">
                        <div className="d-flex gap-2 align-items-center">
                          <Link to={`/cooperatives-list/${item.id}`}>
                            <i
                              className="cursor-pointer"
                              data-toggle="tooltip"
                              data-placement="top"
                              title="View"
                            >
                              <Eye />
                            </i>
                          </Link>
                          <Link
                            to={
                              canEdit("cooperatives")
                                ? `/register/edit/${item.id}`
                                : "#"
                            }
                            onClick={(e) => {
                              if (!canEdit("cooperatives")) {
                                e.preventDefault();
                              }
                            }}
                            style={{
                              pointerEvents: canEdit("cooperatives")
                                ? "auto"
                                : "none",
                              opacity: canEdit("cooperatives") ? 1 : 0.5,
                            }}
                          >
                            <i
                              data-toggle="tooltip"
                              data-placement="top"
                              title={
                                canEdit("cooperatives")
                                  ? "Edit"
                                  : "No permission"
                              }
                            >
                              <Pencil size={20} />
                            </i>
                          </Link>
                          <i
                            className={`fa-solid fa-trash text-danger ${
                              canDelete("cooperatives")
                                ? "cursor-pointer"
                                : "opacity-50"
                            }`}
                            data-toggle="tooltip"
                            data-placement="top"
                            title={
                              canDelete("cooperatives")
                                ? "Delete"
                                : "No permission"
                            }
                            style={{
                              pointerEvents: canDelete("cooperatives")
                                ? "auto"
                                : "none",
                            }}
                            onClick={() => {
                              if (!canDelete("cooperatives")) return;

                              setDeleteId(item.id);
                              setShowDelete(true);
                            }}
                          ></i>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="text-center py-4 text-muted">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <TableFooter
          isMobile={isMobile}
          totalItems={draftCooperatives.length}
          label="Registrations"
          itemsPerPage={5}
          currentPage={currentPage}
          totalPages={totalPages}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
          showAll={showAll}
          onToggleShowAll={() => {
            setShowAll((prev) => !prev);
            setCurrentPage(1);
          }}
          onPageChange={(page) => setCurrentPage(page)}
          className="table-footer"
        />
      </div>
      {showDelete && (
        <DeletePopUp onClose={handleClose} onConfirm={handleDelete} />
      )}
    </>
  );
};

export default Table;
